/**
 * Modal détail complet d'un brassage : répartition sites/salles, capacités,
 * taux d'occupation, effectifs par classe/niveau/pôle + édition de la
 * description en place.
 */
import React, { useState, useEffect } from 'react';
import {
  X, MapPin, Users, Percent, Save, Edit3, CheckCircle2, AlertTriangle,
  Building2, GraduationCap, Layers, Calendar, User as UserIcon, Loader2,
  ChevronDown, ChevronRight, Filter as FilterIcon,
} from 'lucide-react';
import {
  useRepartitionSitesBrassageQuery,
  useModifierDescriptionBrassageMutation,
} from '../../services/brassageApi';

interface Props {
  sessionId: number;
  onClose: () => void;
  isDark?: boolean;
}

function fmtDate(iso: string): string {
  return new Date(iso).toLocaleString('fr-FR', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

function tauxColor(t: number | null): string {
  if (t === null) return 'text-gray-400';
  if (t >= 95) return 'text-red-500';
  if (t >= 80) return 'text-amber-500';
  if (t >= 50) return 'text-emerald-500';
  return 'text-blue-500';
}

export const BrassageDetailsModal: React.FC<Props> = ({ sessionId, onClose, isDark: isDarkProp }) => {
  const [isDark, setIsDark] = useState<boolean>(() =>
    typeof document !== 'undefined' && document.documentElement.classList.contains('dark'));
  useEffect(() => {
    if (isDarkProp !== undefined) { setIsDark(isDarkProp); return; }
    const obs = new MutationObserver(() =>
      setIsDark(document.documentElement.classList.contains('dark')));
    obs.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    return () => obs.disconnect();
  }, [isDarkProp]);
  const { data, isFetching, refetch } = useRepartitionSitesBrassageQuery(sessionId);
  const [modifier, { isLoading: saving }] = useModifierDescriptionBrassageMutation();
  const [editing, setEditing] = useState(false);
  const [descDraft, setDescDraft] = useState('');
  const [expandedSites, setExpandedSites] = useState<Set<string>>(new Set());
  const [feedback, setFeedback] = useState<string | null>(null);

  useEffect(() => {
    if (data && !editing) setDescDraft(data.description || '');
    // Auto-déplier tous les sites au premier chargement
    if (data && expandedSites.size === 0) {
      setExpandedSites(new Set(data.sites.map(s => s.site_nom)));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const toggleSite = (nom: string) => {
    setExpandedSites(prev => {
      const next = new Set(prev);
      if (next.has(nom)) next.delete(nom); else next.add(nom);
      return next;
    });
  };

  const submitDescription = async () => {
    try {
      await modifier({ id: sessionId, description: descDraft }).unwrap();
      setEditing(false);
      setFeedback('Description mise à jour.');
      setTimeout(() => setFeedback(null), 3000);
      refetch();
    } catch (e: any) {
      alert(e?.data?.detail || 'Erreur');
    }
  };

  const bgOverlay = 'bg-black/70';
  const panel = isDark
    ? 'bg-gray-900 text-gray-100 border-gray-800'
    : 'bg-white text-gray-900 border-gray-200';

  return (
    <div className={`fixed inset-0 z-[70] ${bgOverlay} flex items-start sm:items-center justify-center p-3 sm:p-6`}
      onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()}
        className={`w-full max-w-5xl max-h-[92vh] rounded-xl shadow-2xl border-2 flex flex-col ${panel}`}>
        <header className={`px-4 py-3 border-b flex items-center justify-between ${isDark ? 'border-gray-800' : 'border-gray-200'}`}>
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-9 h-9 rounded-lg bg-blue-600 text-white flex items-center justify-center flex-shrink-0">
              <Layers size={18} />
            </div>
            <div className="min-w-0">
              <h2 className="font-bold text-sm truncate">
                Détails du brassage
                {data && ` · ${data.annee_academique} · ${data.mode.replace('_', ' ')}`}
              </h2>
              {data && (
                <div className="text-[11px] text-gray-500 flex items-center gap-2 flex-wrap">
                  <span className="inline-flex items-center gap-0.5">
                    <Calendar size={10} /> {fmtDate(data.date_generation)}
                  </span>
                  {data.genere_par && (
                    <span className="inline-flex items-center gap-0.5">
                      · <UserIcon size={10} /> {data.genere_par}
                    </span>
                  )}
                </div>
              )}
            </div>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700 dark:hover:text-gray-200">
            <X size={18} />
          </button>
        </header>

        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {isFetching && !data ? (
            <div className="text-center py-16 text-sm text-gray-500">
              <Loader2 size={20} className="inline animate-spin mr-2" /> Chargement…
            </div>
          ) : !data ? (
            <div className="text-center py-16 text-sm text-red-500">Erreur de chargement.</div>
          ) : (
            <>
              {/* Description éditable */}
              <section className={`rounded-lg border p-3 ${isDark ? 'bg-gray-800/60 border-gray-700' : 'bg-blue-50/50 border-blue-200'}`}>
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-[11px] font-bold uppercase tracking-widest text-gray-500">Description</h3>
                  {!editing ? (
                    <button onClick={() => setEditing(true)}
                      className="inline-flex items-center gap-1 text-[11px] text-blue-600 dark:text-blue-400 hover:underline">
                      <Edit3 size={11} /> Modifier
                    </button>
                  ) : (
                    <div className="flex gap-2">
                      <button onClick={() => { setEditing(false); setDescDraft(data.description || ''); }}
                        className="text-[11px] px-2 py-0.5 rounded bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300">
                        Annuler
                      </button>
                      <button onClick={submitDescription} disabled={saving}
                        className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded bg-blue-600 text-white font-semibold disabled:opacity-50">
                        {saving ? <Loader2 size={10} className="animate-spin" /> : <Save size={10} />}
                        Enregistrer
                      </button>
                    </div>
                  )}
                </div>
                {editing ? (
                  <textarea value={descDraft} onChange={e => setDescDraft(e.target.value)}
                    rows={5}
                    maxLength={10_000}
                    placeholder="Ex : Brassage session normale S1 2025-2026 sur les sites 1, 2, 3. Concerne L1/L2/L3 tous pôles, hors CS. Fait après validation des inscriptions au 12/08."
                    className={`w-full px-3 py-2 rounded border text-sm ${
                      isDark ? 'bg-gray-900 border-gray-700 text-white' : 'bg-white border-gray-300'
                    }`} />
                ) : (
                  <div className="text-sm whitespace-pre-wrap text-gray-700 dark:text-gray-200 min-h-[20px]">
                    {data.description
                      ? data.description
                      : <span className="italic text-gray-400">Aucune description. Cliquez sur « Modifier » pour en ajouter une.</span>}
                  </div>
                )}
                {feedback && (
                  <div className="mt-2 text-[11px] text-emerald-600 dark:text-emerald-400 inline-flex items-center gap-1">
                    <CheckCircle2 size={11} /> {feedback}
                  </div>
                )}
              </section>

              {/* Totaux consolidés */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <KpiCard icon={<Users size={18} />} label="Étudiants placés"
                  value={data.total_effectif} tone="blue" isDark={!!isDark} />
                <KpiCard icon={<Building2 size={18} />} label="Sites utilisés"
                  value={data.nb_sites_utilises} tone="neutral" isDark={!!isDark} />
                <KpiCard icon={<MapPin size={18} />} label="Salles utilisées"
                  value={data.total_salles_utilisees} tone="neutral" isDark={!!isDark} />
                <KpiCard icon={<Percent size={18} />}
                  label={`Occupation (${data.total_places_libres} places libres)`}
                  value={data.total_taux_occupation !== null ? `${data.total_taux_occupation}%` : '—'}
                  tone={data.total_taux_occupation && data.total_taux_occupation >= 95 ? 'red'
                        : data.total_taux_occupation && data.total_taux_occupation >= 80 ? 'amber'
                        : 'green'} isDark={!!isDark} />
              </div>
              <div className={`rounded-lg p-2 text-[11px] flex items-center gap-2 ${
                isDark ? 'bg-gray-800/60 text-gray-400' : 'bg-gray-50 text-gray-600'
              }`}>
                <span className="tabular-nums">Capacité totale : <strong>{data.total_capacite}</strong> places</span>
                {data.placements_exclus > 0 && (
                  <span className="inline-flex items-center gap-1 text-amber-500">
                    <AlertTriangle size={11} />
                    {data.placements_exclus} placement(s) masqué(s) (étudiants devenus exclus)
                  </span>
                )}
                {data.sites_non_utilises.length > 0 && (
                  <span className="text-gray-500">
                    · Sites non utilisés : {data.sites_non_utilises.join(', ')}
                  </span>
                )}
              </div>

              {/* Rappel filtres appliqués à la génération */}
              {Object.keys(data.filtres || {}).length > 0 && (
                <section className={`rounded-lg border p-3 text-xs ${
                  isDark ? 'bg-gray-800/60 border-gray-700' : 'bg-gray-50 border-gray-200'
                }`}>
                  <div className="flex items-center gap-1 text-[10px] uppercase tracking-widest text-gray-500 font-bold mb-1.5">
                    <FilterIcon size={11} /> Filtres appliqués à la génération
                  </div>
                  <FiltresRappel filtres={data.filtres} />
                </section>
              )}

              {/* Détails par site */}
              <section>
                <h3 className="text-[11px] font-bold uppercase tracking-widest text-gray-500 mb-2">
                  Répartition détaillée par site
                </h3>
                <div className="space-y-2">
                  {data.sites.map(site => {
                    const open = expandedSites.has(site.site_nom);
                    return (
                      <div key={site.site_nom}
                        className={`rounded-lg border ${isDark ? 'bg-gray-900 border-gray-800' : 'bg-white border-gray-200'}`}>
                        <button onClick={() => toggleSite(site.site_nom)}
                          className="w-full flex items-center gap-3 px-3 py-2.5 text-left hover:bg-gray-50 dark:hover:bg-gray-800 rounded-t-lg">
                          {open ? <ChevronDown size={14} className="text-gray-400" /> : <ChevronRight size={14} className="text-gray-400" />}
                          <div className="w-8 h-8 rounded-lg bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 flex items-center justify-center flex-shrink-0">
                            <Building2 size={16} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="font-semibold text-sm">{site.site_nom}</div>
                            <div className="text-[11px] text-gray-500 flex items-center gap-2 flex-wrap">
                              <span><strong>{site.effectif_site}</strong> / {site.capacite_totale} places</span>
                              <span>· {site.nb_salles_utilisees} salle(s)</span>
                              {site.places_libres > 0 && (
                                <span>· {site.places_libres} places libres</span>
                              )}
                            </div>
                          </div>
                          {site.taux_occupation !== null && (
                            <div className="flex flex-col items-end flex-shrink-0">
                              <span className={`text-lg font-bold tabular-nums ${tauxColor(site.taux_occupation)}`}>
                                {site.taux_occupation}%
                              </span>
                              <span className="text-[10px] text-gray-500">occupation</span>
                            </div>
                          )}
                        </button>
                        {open && (
                          <div className={`px-3 pb-3 border-t space-y-3 ${isDark ? 'border-gray-800' : 'border-gray-100'}`}>
                            {/* Salles */}
                            <div>
                              <div className="text-[10px] font-semibold uppercase tracking-wider text-gray-500 mt-2 mb-1">
                                Salles ({site.salles.length})
                              </div>
                              <div className="overflow-x-auto">
                                <table className="w-full text-xs">
                                  <thead>
                                    <tr className={`text-[10px] uppercase tracking-wider ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
                                      <th className="text-left py-1 pr-2">Salle</th>
                                      <th className="text-right py-1 pr-2">Capacité</th>
                                      <th className="text-right py-1 pr-2">Placés</th>
                                      <th className="text-right py-1 pr-2">Libres</th>
                                      <th className="text-right py-1">Occupation</th>
                                    </tr>
                                  </thead>
                                  <tbody className="divide-y divide-gray-100 dark:divide-gray-800/60">
                                    {site.salles.map(salle => (
                                      <tr key={salle.salle_nom}>
                                        <td className="py-1 pr-2 font-medium">{salle.salle_nom}</td>
                                        <td className="py-1 pr-2 text-right tabular-nums">{salle.capacite}</td>
                                        <td className="py-1 pr-2 text-right tabular-nums font-semibold">{salle.effectif}</td>
                                        <td className="py-1 pr-2 text-right tabular-nums text-gray-500">{salle.places_libres ?? '—'}</td>
                                        <td className={`py-1 text-right tabular-nums font-semibold ${tauxColor(salle.taux_remplissage)}`}>
                                          {salle.taux_remplissage !== null ? `${salle.taux_remplissage}%` : '—'}
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            </div>
                            {/* Répartition classes / niveaux / pôles */}
                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                              <RepartitionPuces titre="Pôles" data={site.poles} Icone={GraduationCap} isDark={!!isDark} />
                              <RepartitionPuces titre="Niveaux" data={site.niveaux} Icone={Layers} isDark={!!isDark} />
                              <RepartitionPuces titre="Classes" data={site.classes} Icone={Users} isDark={!!isDark} />
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </section>
            </>
          )}
        </div>

        <footer className={`px-4 py-3 border-t flex justify-end gap-2 ${isDark ? 'border-gray-800' : 'border-gray-200'}`}>
          <button onClick={onClose}
            className={`px-3 py-1.5 text-sm rounded ${isDark ? 'bg-gray-800 hover:bg-gray-700' : 'bg-gray-100 hover:bg-gray-200'}`}>
            Fermer
          </button>
        </footer>
      </div>
    </div>
  );
};

// ─── Sous-composants ────────────────────────────────────────────────────

const KpiCard: React.FC<{
  icon: React.ReactNode; label: string; value: React.ReactNode;
  tone: 'blue' | 'green' | 'amber' | 'red' | 'neutral';
  isDark: boolean;
}> = ({ icon, label, value, tone, isDark }) => {
  const tones: Record<string, string> = {
    blue: 'text-blue-600 dark:text-blue-400',
    green: 'text-emerald-600 dark:text-emerald-400',
    amber: 'text-amber-600 dark:text-amber-400',
    red: 'text-red-600 dark:text-red-400',
    neutral: 'text-gray-700 dark:text-gray-200',
  };
  return (
    <div className={`rounded-lg border p-3 ${isDark ? 'bg-gray-900 border-gray-800' : 'bg-white border-gray-200'}`}>
      <div className="flex items-center gap-2">
        <div className={tones[tone]}>{icon}</div>
        <div className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold flex-1 truncate">{label}</div>
      </div>
      <div className={`text-2xl font-bold mt-1 tabular-nums ${tones[tone]}`}>{value}</div>
    </div>
  );
};

const RepartitionPuces: React.FC<{
  titre: string; data: Array<{ label: string; nb: number }>;
  Icone: React.ComponentType<any>; isDark: boolean;
}> = ({ titre, data, Icone, isDark }) => {
  if (!data.length) return null;
  return (
    <div className={`rounded p-2 text-[11px] ${isDark ? 'bg-gray-800/60' : 'bg-gray-50'}`}>
      <div className="text-[10px] font-semibold uppercase tracking-wider text-gray-500 mb-1 inline-flex items-center gap-1">
        <Icone size={10} /> {titre}
      </div>
      <div className="flex flex-wrap gap-1">
        {data.map(d => (
          <span key={d.label}
            className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded ${
              isDark ? 'bg-gray-900 text-gray-300' : 'bg-white border border-gray-200 text-gray-700'
            }`}>
            {d.label} <span className="tabular-nums font-semibold text-blue-500">{d.nb}</span>
          </span>
        ))}
      </div>
    </div>
  );
};

const FiltresRappel: React.FC<{ filtres: any }> = ({ filtres }) => {
  const items: React.ReactNode[] = [];
  if (filtres.niveaux?.length) items.push(<span key="n"><strong>Niveaux :</strong> {filtres.niveaux.join(', ')}</span>);
  if (filtres.poles?.length) items.push(<span key="p"><strong>Pôles :</strong> {filtres.poles.join(', ')}</span>);
  if (filtres.classes?.length) items.push(<span key="c"><strong>Classes :</strong> {filtres.classes.join(', ')}</span>);
  if (filtres.sites?.length) items.push(<span key="s"><strong>Sites ciblés :</strong> {filtres.sites.join(', ')}</span>);
  if (filtres.taux_remplissage) items.push(<span key="t"><strong>Taux remplissage :</strong> {filtres.taux_remplissage}%</span>);
  if (filtres.seulement_a_rattraper) items.push(<span key="r" className="text-amber-500">Mode rattrapage uniquement</span>);
  if (filtres.exclure_absents_confirmes === false) items.push(<span key="a" className="text-amber-500">Absents confirmés inclus (override)</span>);
  if (filtres.matricules_a_exclure?.length) items.push(<span key="m"><strong>{filtres.matricules_a_exclure.length}</strong> exclusion(s) manuelle(s)</span>);
  if (!items.length) return <span className="text-gray-500 italic">Aucun filtre spécifique.</span>;
  return <div className="flex flex-wrap gap-x-4 gap-y-1">{items}</div>;
};

export default BrassageDetailsModal;
